import React, { useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router';
import GameSkeleton, { DailyChallengeLoadingScreen } from '../GameSkeleton/GameSkeleton';
import { GameplaySectionShell } from '../GameplaySection/GameplaySectionShell';
import { resolveRouteLoadingSurface, type RouteLoadingSurface } from './routeLoadingSurface';

const LoadingLine: React.FC<{ className: string }> = ({ className }) => (
  <div className={`skeleton-shimmer rounded-md ${className}`} />
);

export const PartnerPortalHydrateFallback: React.FC = () => (
  <div
    className="partner-portal-viewport-shell relative flex w-full overflow-hidden bg-primary"
    data-skeleton-surface="portal"
    aria-hidden="true"
  >
    <aside className="hidden h-full w-60 shrink-0 flex-col border-r border-outline-default bg-secondary p-4 md:flex">
      <div className="mb-8 flex items-center gap-3">
        <div className="skeleton-shimmer h-9 w-9 rounded-lg" />
        <LoadingLine className="h-5 w-24" />
      </div>
      <div className="space-y-3">
        {Array.from({ length: 6 }, (_, index) => (
          <div key={`portal-nav-skeleton-${index}`} className="flex items-center gap-3 py-2">
            <div className="skeleton-shimmer h-5 w-5 rounded" />
            <LoadingLine className={index === 2 ? 'h-4 w-28' : 'h-4 w-20'} />
          </div>
        ))}
      </div>
    </aside>

    <main className="relative flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      <div className="min-h-0 flex-1 overflow-hidden px-4 pt-6 pb-24 md:overflow-auto md:py-8">
        <div className="mb-6 flex items-center justify-between">
          <LoadingLine className="h-7 w-36" />
          <div className="skeleton-shimmer h-10 w-28 rounded-full" />
        </div>
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="rounded-xl border border-outline-default bg-raised p-5 lg:col-span-2">
            <LoadingLine className="mb-4 h-5 w-40" />
            <LoadingLine className="mb-3 h-4 w-full" />
            <LoadingLine className="mb-6 h-4 w-3/4" />
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="skeleton-shimmer h-24 rounded-lg" />
              <div className="skeleton-shimmer h-24 rounded-lg" />
            </div>
          </div>
          <div className="rounded-xl border border-outline-default bg-raised p-5">
            <LoadingLine className="mb-5 h-5 w-28" />
            <div className="space-y-3">
              <LoadingLine className="h-4 w-full" />
              <LoadingLine className="h-4 w-5/6" />
              <LoadingLine className="h-4 w-2/3" />
            </div>
          </div>
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-0 grid h-20 grid-cols-4 border-t border-outline-default bg-raised px-3 md:hidden">
        {Array.from({ length: 4 }, (_, index) => (
          <div
            key={`portal-mobile-nav-skeleton-${index}`}
            className="flex flex-col items-center justify-center gap-2"
          >
            <div className="skeleton-shimmer h-5 w-5 rounded" />
            <LoadingLine className="h-3 w-12" />
          </div>
        ))}
      </div>
    </main>
  </div>
);

const RootGameHydrateFallback: React.FC<{
  mode: 'quiz' | 'daily' | 'endless';
}> = ({ mode }) => {
  const location = useLocation();
  const isIframeView = new URLSearchParams(location.search).get('view') === 'iframe';

  return (
    <GameplaySectionShell className="px-5 md:px-10 md:pb-8" isIframeView={isIframeView}>
      {mode === 'daily' ? (
        <DailyChallengeLoadingScreen />
      ) : (
        <GameSkeleton mode={mode} showGenerationTag={mode !== 'quiz'} />
      )}
    </GameplaySectionShell>
  );
};

const DefaultRootHydrateFallback: React.FC = () => (
  <div className="flex min-h-screen-safe w-full flex-col bg-primary" aria-hidden="true">
    <header className="flex h-16 items-center justify-between border-b border-outline-default px-5 md:px-10">
      <LoadingLine className="h-7 w-28" />
      <div className="flex gap-3">
        <LoadingLine className="h-9 w-20 rounded-full" />
        <LoadingLine className="h-9 w-24 rounded-full" />
      </div>
    </header>
    <main className="mx-auto flex w-full max-w-6xl flex-1 flex-col px-5 py-10 md:px-10 md:py-16">
      <LoadingLine className="mb-5 h-10 w-2/3 max-w-xl" />
      <LoadingLine className="mb-3 h-5 w-full max-w-2xl" />
      <LoadingLine className="mb-10 h-5 w-4/5 max-w-xl" />
      <div className="grid gap-5 md:grid-cols-3">
        <div className="skeleton-shimmer h-48 rounded-2xl md:col-span-2" />
        <div className="skeleton-shimmer h-48 rounded-2xl" />
      </div>
    </main>
  </div>
);

export const ScheduledQuizHydrateFallback: React.FC = () => (
  <div className="flex min-h-two-thirds items-center justify-center px-6 text-center">
    <div className="flex w-full max-w-md flex-col items-center">
      <div className="skeleton-shimmer mb-3 h-6 w-48 rounded-md" />
      <div className="skeleton-shimmer h-4 w-64 rounded-md" />
    </div>
  </div>
);

export const SubscriptionConfirmationHydrateFallback: React.FC = () => (
  <div className="min-h-screen-safe bg-primary px-4 py-12 md:py-20">
    <div className="mx-auto grid max-w-5xl gap-10 md:grid-cols-2 md:gap-16">
      <div className="flex flex-col justify-center">
        <div className="skeleton-shimmer mb-3 h-4 w-32 rounded-md" />
        <div className="skeleton-shimmer mb-4 h-10 w-64 rounded-md" />
        <div className="skeleton-shimmer h-5 w-80 rounded-md" />
      </div>

      <div
        className="flex flex-col items-center justify-center gap-6 rounded-2xl bg-secondary p-6 shadow-default md:p-8"
        style={{ minHeight: 300 }}
      >
        <div className="skeleton-shimmer h-6 w-48 rounded-md" />
        <div className="skeleton-shimmer h-4 w-64 rounded-md" />
        <div className="skeleton-shimmer mt-4 h-12 w-full max-w-xs rounded-full" />
      </div>
    </div>
  </div>
);

const ROOT_FALLBACKS: Record<RouteLoadingSurface, React.FC> = {
  portal: PartnerPortalHydrateFallback,
  quiz: () => <RootGameHydrateFallback mode="quiz" />,
  daily: () => <RootGameHydrateFallback mode="daily" />,
  endless: () => <RootGameHydrateFallback mode="endless" />,
  scheduled: ScheduledQuizHydrateFallback,
  confirmation: SubscriptionConfirmationHydrateFallback,
  default: DefaultRootHydrateFallback,
};

const RootLoadingSurface: React.FC<{ surface: RouteLoadingSurface }> = ({ surface }) => {
  const Fallback = ROOT_FALLBACKS[surface];

  return (
    <div data-root-hydrate-fallback="true" aria-busy="true">
      <span className="sr-only" role="status">
        Loading page
      </span>
      <div data-route-loading-variant={surface}>
        <Fallback />
      </div>
    </div>
  );
};

/** The static SPA document is built once at / and hydrates on any supported deep link. */
export const DocumentHydrateFallback: React.FC = () => {
  const location = useLocation();
  const [hasHydrated, setHasHydrated] = useState(false);

  useLayoutEffect(() => {
    setHasHydrated(true);
  }, []);

  return (
    <RootLoadingSurface
      surface={hasHydrated ? resolveRouteLoadingSurface(location.pathname) : 'default'}
    />
  );
};

/** Client loading can select a route surface once document hydration has completed. */
export const RootHydrateFallback: React.FC = () => {
  const location = useLocation();
  return <RootLoadingSurface surface={resolveRouteLoadingSurface(location.pathname)} />;
};
